import React, { useMemo } from 'react'
import { NavLink } from 'react-router-dom'

// constants
import { IMAGE_CAPTURE_STEPS, ROUTES } from '../../constants'
import { IMAGE_TYPES } from './constants'

// components
import Logo from '../../components/logo'
import BackButton from '../../components/back-button'
import Loader from '../../components/loader'
import ImageCapture from './components/image-capture'

// hooks
import useCaptureSteps from './hooks/use-capture-steps'
import useCapturedImage from './hooks/use-captured-image'
import useCaptureGuidelines from './hooks/use-capture-guidelines'
import CaptureGuidelines from './components/capture-guidelines'

export default function ImageCapturePage() {
    const {
        activeStep,
        handleLoadNextStep,
        handleLoadPreviousStep,
        handleLoadGlobalCapture
    } = useCaptureSteps();

    const {
        globalCrownImage,
        globalLeftImage,
        globalRightImage,
        isAnalysisCreated,
        isImageUploadSuccessful,
        resetStateToInitials,
        setGlobalCrownImage,
        setGlobalLeftImage,
        setGlobalRightImage,
        uploadErrors,
        uploadImages,
        uploadingImages,
        isImageStreamLoaded,
        setIsImageStreamLoaded
    } = useCapturedImage();

    const { showGuidelines, setShowGuidelines } = useCaptureGuidelines()

    const shouldRenderUploadErrors = activeStep === IMAGE_CAPTURE_STEPS.IMAGE_UPLOAD;

    const loaderMessage = useMemo(() => {
        if (!uploadingImages) return "";

        if (isAnalysisCreated) return "Uploading images, "
        return "Creating analysis, "
    }, [isAnalysisCreated, uploadingImages])

    const hasUploadFailed = useMemo(() => {
        return Object.values(uploadErrors).some(error => error);
    }, [uploadErrors])

    if (uploadingImages) {
        return (
            <Loader
                message={loaderMessage}
            />
        )
    }

    return (
        <main main className='relative flex-grow flex flex-col px-4' >
            <BackButton handlePrevNavigation={isImageUploadSuccessful ? () => { } : handleLoadPreviousStep} />
            <div className='flex justify-center py-10'>
                <Logo className="h-14" />
            </div>
            <div className='flex justify-center'>
                <div className='flex relative px-6 items-center'>
                    <h6 className='text-center font-semibold text-xl text-text-1 capitalize flex-shrink-0 mb-4'>
                        {activeStep?.split("_")?.join(" ")}
                    </h6>
                    {
                        activeStep !== IMAGE_CAPTURE_STEPS.IMAGE_UPLOAD && isImageStreamLoaded && (
                            <svg onClick={() => setShowGuidelines(true)} className="absolute right-0 top-3.5 -translate-y-1/2" width={14} height={14} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM169.8 165.3c7.9-22.3 29.1-37.3 52.8-37.3l58.3 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24l0-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1l-58.3 0c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" fill='#B7E5E4' /></svg>
                        )}
                </div>
            </div>
            {
                Object.entries(uploadErrors).map(([errorType, error], index) => {
                    if (error && shouldRenderUploadErrors) {
                        return (
                            <div className='flex flex-col'>
                                <p key={index} className='text-red-400 font-bold text-sm text-center'>{errorType?.split("_")?.join(" ")} error:</p>
                                <p key={index} className='text-red-400 font-bold text-sm text-center'>{error}</p>
                            </div>
                        )
                    }
                    return null
                })
            }
            {
                isImageUploadSuccessful && shouldRenderUploadErrors && (<div className='flex flex-col gap-10 items-center justify-center w-full'>
                    <p className='text-green-400 font-bold text-sm text-center'>Images uploaded successfully</p>
                    <h5 className='text-text-1 font-bold text-xl text-center'>The results will be sent to your client in 1 min. <br /> You should review them together & encourage them to follow the treatment suggestions!</h5>
                </div>)
            }
            {
                (
                    activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_CROWN ||
                    activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_LEFT ||
                    activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_RIGHT) && (
                    <ImageCapture
                        isImageStreamLoaded={isImageStreamLoaded}
                        setIsImageStreamLoaded={setIsImageStreamLoaded}
                        setCapturedImage={
                            activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_CROWN ? setGlobalCrownImage :
                                activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_LEFT ? setGlobalLeftImage : setGlobalRightImage
                        }
                        capturedImage={
                            activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_CROWN ? globalCrownImage :
                                activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_LEFT ? globalLeftImage : globalRightImage
                        }
                        imageTypeToCapture={
                            activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_CROWN ?
                                IMAGE_TYPES.CROWN :
                                activeStep === IMAGE_CAPTURE_STEPS.GLOBAL_LEFT ?
                                    IMAGE_TYPES.LEFT : IMAGE_TYPES.RIGHT
                        }
                        onNext={handleLoadNextStep}
                    />
                )
            }
            {
                activeStep === IMAGE_CAPTURE_STEPS.IMAGE_UPLOAD && (
                    isImageUploadSuccessful ? (
                        <div className='flex-grow flex items-start justify-center w-full pt-10' >
                            <NavLink
                                className="mt-5 px-8 py-3 font-bold rounded-lg text-background-1 bg-text-1 cursor-pointer uppercase focus:outline-none"
                                to={ROUTES.HOME}
                            >
                                End this session
                            </NavLink>
                        </div>
                    ) : (
                        <div className='flex-grow flex flex-col items-center justify-start w-full pt-20' >
                            <button
                                disabled={uploadingImages}
                                className="mt-5 w-[224px] py-3 bg-[#f4831f] font-semibold rounded-[5px] text-white cursor-pointer capitalize focus:outline-none"
                                onClick={uploadImages}
                            >
                                {hasUploadFailed ? "Retry upload" : "Upload images"}
                            </button>
                            {hasUploadFailed && (
                                <button
                                    className="mt-5 px-8 py-3 bg-red-700 font-bold rounded-lg text-white cursor-pointer uppercase focus:outline-none"
                                    onClick={() => {
                                        resetStateToInitials();
                                        handleLoadGlobalCapture()
                                    }}
                                >
                                    Recapture images
                                </button>
                            )}
                        </div>
                    )
                )
            }
            {showGuidelines && isImageStreamLoaded && (
                <CaptureGuidelines
                    onAcceptGuidelines={() => setShowGuidelines(false)}
                />
            )}
        </main>
    )
};